import type { TFunction } from "i18next";
import {
  DAYS_PER_MONTH,
  PricingModelItem,
  PurchaseStudentType,
  renewalPeriodOfDays,
} from "./model";

/** Human-readable label for the raw purchase type identifier. */
export function formatPurchaseStudentType(type: PurchaseStudentType) {
  return type === "one-on-one" ? "One-on-one" : "Self-study";
}

export const PRICINGMODEL_OBJ = {
  name: {
    label: "Name",
    type: "text",
    required: true,
    maxLength: 64,
  },
  code: {
    label: "Code",
    type: "text",
    required: true,
    maxLength: 16,
  },
  description: {
    label: "Description",
    type: "text",
    required: true,
    maxLength: 512,
  },
  target_audience: {
    label: "Target audience",
    type: "text",
    required: false,
    maxLength: 512,
  },
  benefits: {
    label: "Benefits",
    type: "text",
    required: false,
    maxLength: 1024,
  },
  team: {
    label: "Team",
    required: false,
    maxLength: 64,
  },
  type: {
    label: "Pricing type",
    required: true,
    default: "one-on-one",
    options: ["one-on-one", "self-study"],
  },
};

export const PRICINGMODELITEM_OBJ = {
  name: {
    label: "Item Name",
    type: "text",
    required: true,
    maxLength: 64,
  },
  number: {
    label: "Lessons",
    type: "number",
    required: true,
    minValue: 1,
    maxValue: 99, //For now I don't think we'll have a package for an individual with more than 99 hours in it.
  },
  price: {
    label: "Price",
    type: "number",
    required: true,
    minValue: 1,
    maxValue: 9999,
  },
  ai_tokens: {
    label: "AI Tokens",
    type: "number",
    required: false,
    minValue: 0,
  },
  length: {
    label: "Length (days)",
    type: "number",
    required: true,
    minValue: 1,
    maxValue: 365, // Annual subscription is maximum for now.
  },
};

export function periodToString(t: TFunction, period?: number | null) {
  if (period === undefined || period === null) {
    return t("Common.billingFrequency.unknownPeriod");
  }
  switch (renewalPeriodOfDays(period)) {
    case "monthly":
      return t("Common.billingFrequency.perMonth");
    case "annual":
      return t("Common.billingFrequency.perYear");
    case null:
      return t("Common.billingFrequency.perDays", { count: period });
  }
}

// The price with its renewal period as one phrase, "$29 per month".
export function formatRenewingPrice(t: TFunction, price: string, periodDays: number) {
  switch (renewalPeriodOfDays(periodDays)) {
    case "monthly":
      return t("Common.billingFrequency.pricePerMonth", { price });
    case "annual":
      return t("Common.billingFrequency.pricePerYear", { price });
    case null:
      return t("Common.billingFrequency.pricePerDays", { price, count: periodDays });
  }
}

export const calculatePrice = (item: PricingModelItem) => {
  const count =
    item.purchase_type === "subscription"
      ? (item.length as number) / DAYS_PER_MONTH
      : (item.number as number);
  return item.price / count;
};
